import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

import { stockKeys } from "./query-keys";

/** A deduped base-product suggestion (one entry per sku, not per sku×marketplace). */
export interface SkuSearchItem {
  sku: string;
  product_name: string;
}

/**
 * Searches base products by name/SKU via `/v1/stock?search=`. The stock endpoint
 * returns one row per sku×marketplace, so we dedupe by `sku` into a flat list of
 * `{ sku, product_name }`.
 *
 * When the trimmed term is empty the query returns an initial list (no `search`
 * param), so the picker can show suggestions on focus before any typing. Typing
 * a term filters that list. Always enabled.
 */
export function useSkuSearch(term: string) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: stockKeys.search(trimmed),
    queryFn: async (): Promise<SkuSearchItem[]> => {
      const { data, error } = await apiClient.GET("/v1/stock", {
        params: { query: { limit: 20, ...(trimmed ? { search: trimmed } : {}) } },
      });
      if (error) throw error;

      const seen = new Set<string>();
      const items: SkuSearchItem[] = [];
      for (const row of data?.items ?? []) {
        const sku = row.sku;
        if (!sku || seen.has(sku)) continue;
        seen.add(sku);
        items.push({ sku, product_name: row.product_name ?? "" });
      }
      return items;
    },
    staleTime: 10_000,
  });
}
